import React, { useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';

const PageContainer = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  color: #333;
  text-align: center;
`;

const ModuleContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
`;

const ModuleCard = styled.div`
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 1.5rem;
  background-color: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }
`;

const ModuleTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #333;
  border-bottom: 2px solid #007bff;
  padding-bottom: 0.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #555;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  resize: vertical;
  min-height: 100px;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #0069d9;
    transform: translateY(-1px);
  }
  
  &:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
`;

const Slider = styled.input`
  width: 100%;
  margin: 0.5rem 0;
`;

const SliderValue = styled.div`
  text-align: right;
  font-size: 0.8rem;
  color: #666;
`;

const SearchPage = () => {
  // IP大脑模块状态
  const [ipBrain, setIpBrain] = useState({
    keyword: '',
    platform: 'douyin',
    content: '',
    creativity: 80
  });

  // AI数字人模块状态
  const [aiPerson, setAiPerson] = useState({
    model: 'default',
    emotion: 'neutral',
    speechRate: 1.0,
    pitch: 1.0
  });

  // 智能剪辑模块状态
  const [videoEdit, setVideoEdit] = useState({
    videoSource: 'upload',
    subtitle: true,
    backgroundMusic: true,
    transitionEffect: 'fade'
  });

  // 自动发布模块状态
  const [autoPublish, setAutoPublish] = useState({
    platforms: {
      douyin: false,
      kuaishou: false,
      bilibili: false,
      wechat: false
    },
    schedule: 'immediate'
  });

  // 处理IP大脑表单变化
  const handleIpBrainChange = (e) => {
    const { name, value } = e.target;
    setIpBrain(prev => ({ ...prev, [name]: value }));
  };

  // 处理AI数字人表单变化
  const handleAiPersonChange = (e) => {
    const { name, value } = e.target;
    setAiPerson(prev => ({ ...prev, [name]: value }));
  };

  // 处理智能剪辑表单变化
  const handleVideoEditChange = (e) => {
    const { name, checked, value } = e.target;
    setVideoEdit(prev => ({
      ...prev,
      [name]: e.target.type === 'checkbox' ? checked : value
    }));
  };

  // 处理自动发布表单变化
  const handleAutoPublishChange = (e) => {
    const { name, checked, value } = e.target;
    if (name === 'platform') {
      setAutoPublish(prev => ({
        ...prev,
        platforms: {
          ...prev.platforms,
          [value]: checked
        }
      }));
    } else {
      setAutoPublish(prev => ({ ...prev, [name]: value }));
    }
  };

  // 提交搜索请求
  const handleSearch = async () => {
    try {
      const response = await axios.post('/api/v1/search/ip', {
        ipBrain,
        aiPerson,
        videoEdit,
        autoPublish
      });
      console.log('搜索结果:', response.data);
      // 处理搜索结果
    } catch (error) {
      console.error('搜索失败:', error);
    }
  };

  return (
    <PageContainer>
      <PageTitle>超级IP智能体</PageTitle>
      
      <ModuleContainer>
        {/* IP大脑模块 */}
        <ModuleCard>
          <ModuleTitle>IP大脑</ModuleTitle>
          <FormGroup>
            <Label>搜索关键词</Label>
            <Input
              type="text"
              name="keyword"
              value={ipBrain.keyword}
              onChange={handleIpBrainChange}
              placeholder="输入搜索关键词"
            />
          </FormGroup>
          <FormGroup>
            <Label>平台</Label>
            <Select
              name="platform"
              value={ipBrain.platform}
              onChange={handleIpBrainChange}
            >
              <option value="douyin">抖音</option>
              <option value="kuaishou">快手</option>
              <option value="bilibili">B站</option>
              <option value="wechat">微信</option>
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>创意内容</Label>
            <TextArea
              name="content"
              value={ipBrain.content}
              onChange={handleIpBrainChange}
              placeholder="输入创意内容"
            />
          </FormGroup>
          <FormGroup>
            <Label>创意程度</Label>
            <Slider
              type="range"
              name="creativity"
              min="0"
              max="100"
              value={ipBrain.creativity}
              onChange={handleIpBrainChange}
            />
            <SliderValue>{ipBrain.creativity}%</SliderValue>
          </FormGroup>
          <Button>生成创意</Button>
        </ModuleCard>

        {/* AI数字人模块 */}
        <ModuleCard>
          <ModuleTitle>AI数字人</ModuleTitle>
          <FormGroup>
            <Label>数字人模型</Label>
            <Select
              name="model"
              value={aiPerson.model}
              onChange={handleAiPersonChange}
            >
              <option value="default">默认模型</option>
              <option value="professional">专业版</option>
              <option value="creative">创意版</option>
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>情感风格</Label>
            <Select
              name="emotion"
              value={aiPerson.emotion}
              onChange={handleAiPersonChange}
            >
              <option value="neutral">中性</option>
              <option value="happy">开心</option>
              <option value="excited">兴奋</option>
              <option value="calm">平静</option>
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>语速</Label>
            <Slider
              type="range"
              name="speechRate"
              min="0.5"
              max="2"
              step="0.1"
              value={aiPerson.speechRate}
              onChange={handleAiPersonChange}
            />
            <SliderValue>{aiPerson.speechRate}x</SliderValue>
          </FormGroup>
          <FormGroup>
            <Label>音调</Label>
            <Slider
              type="range"
              name="pitch"
              min="0.5"
              max="2"
              step="0.1"
              value={aiPerson.pitch}
              onChange={handleAiPersonChange}
            />
            <SliderValue>{aiPerson.pitch}x</SliderValue>
          </FormGroup>
          <Button>生成数字人</Button>
        </ModuleCard>

        {/* 智能剪辑模块 */}
        <ModuleCard>
          <ModuleTitle>智能剪辑</ModuleTitle>
          <FormGroup>
            <Label>视频来源</Label>
            <Select
              name="videoSource"
              value={videoEdit.videoSource}
              onChange={handleVideoEditChange}
            >
              <option value="upload">本地上传</option>
              <option value="template">模板生成</option>
              <option value="url">视频链接</option>
            </Select>
          </FormGroup>
          <FormGroup>
            <CheckboxLabel>
              <input
                type="checkbox"
                name="subtitle"
                checked={videoEdit.subtitle}
                onChange={handleVideoEditChange}
              />
              自动添加字幕
            </CheckboxLabel>
          </FormGroup>
          <FormGroup>
            <CheckboxLabel>
              <input
                type="checkbox"
                name="backgroundMusic"
                checked={videoEdit.backgroundMusic}
                onChange={handleVideoEditChange}
              />
              添加背景音乐
            </CheckboxLabel>
          </FormGroup>
          <FormGroup>
            <Label>转场效果</Label>
            <Select
              name="transitionEffect"
              value={videoEdit.transitionEffect}
              onChange={handleVideoEditChange}
            >
              <option value="fade">淡入淡出</option>
              <option value="slide">滑动</option>
              <option value="zoom">缩放</option>
              <option value="none">无</option>
            </Select>
          </FormGroup>
          <Button>智能剪辑</Button>
        </ModuleCard>

        {/* 自动发布模块 */}
        <ModuleCard>
          <ModuleTitle>自动发布</ModuleTitle>
          <FormGroup>
            <Label>发布平台</Label>
            <CheckboxGroup>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  name="platform"
                  value="douyin"
                  checked={autoPublish.platforms.douyin}
                  onChange={handleAutoPublishChange}
                />
                抖音
              </CheckboxLabel>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  name="platform"
                  value="kuaishou"
                  checked={autoPublish.platforms.kuaishou}
                  onChange={handleAutoPublishChange}
                />
                快手
              </CheckboxLabel>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  name="platform"
                  value="bilibili"
                  checked={autoPublish.platforms.bilibili}
                  onChange={handleAutoPublishChange}
                />
                B站
              </CheckboxLabel>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  name="platform"
                  value="wechat"
                  checked={autoPublish.platforms.wechat}
                  onChange={handleAutoPublishChange}
                />
                微信
              </CheckboxLabel>
            </CheckboxGroup>
          </FormGroup>
          <FormGroup>
            <Label>发布时间</Label>
            <Select
              name="schedule"
              value={autoPublish.schedule}
              onChange={handleAutoPublishChange}
            >
              <option value="immediate">立即发布</option>
              <option value="scheduled">定时发布</option>
            </Select>
          </FormGroup>
          <Button>一键发布</Button>
        </ModuleCard>
      </ModuleContainer>

      <Button onClick={handleSearch} style={{ width: '100%', padding: '1rem', fontSize: '1.1rem' }}>
        一键生成专属IP
      </Button>
    </PageContainer>
  );
};

export default SearchPage;